import Box from '@mui/material/Box';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { NAV_ITEMS } from '../../Layout/navItems';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

function flattenNavItems(items, trail = [], moduleIcon = null) {
    return items.flatMap((item) => {
        const path = [...trail, item.label];
        const icon = moduleIcon ?? item.icon;
        if (item.subItems?.length) {
            return flattenNavItems(item.subItems, path, icon);
        }
        if (!item.path) return [];
        return [{
            label: item.label,
            path: item.path,
            icon,
            group: trail[0] ?? item.label,
            breadcrumb: path.join(' › '),
            subLabel: path.slice(1).join(' › '),
        }];
    });
}

const FLAT_ACTIONS = flattenNavItems(NAV_ITEMS);

const QuickActions = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (open) {
            setQuery('');
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setOpen((prev) => !prev);
            } else if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const groupedResults = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = q
            ? FLAT_ACTIONS.filter((action) => action.breadcrumb.toLowerCase().includes(q))
            : FLAT_ACTIONS;

        const groups = [];
        const groupIndex = new Map();
        matches.forEach((action) => {
            if (!groupIndex.has(action.group)) {
                groupIndex.set(action.group, groups.length);
                groups.push({ group: action.group, icon: action.icon, items: [] });
            }
            groups[groupIndex.get(action.group)].items.push(action);
        });
        return groups;
    }, [query]);

    const handleSelect = (path) => {
        navigate(path);
        setOpen(false);
    };

    return (
        <Box sx={{ position: 'relative' }} ref={wrapperRef}>
            <Box
                component="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-label="Quick Actions"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    px: '0.75rem',
                    py: '0.5rem',
                    bgcolor: 'rgba(241, 245, 249, 0.5)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                    color: '#64748b',
                    cursor: 'pointer',
                    width: { xs: 'auto', md: '220px' },
                    fontFamily: 'inherit',
                    transition: 'all 150ms',
                    '&:hover': { borderColor: '#cbd5e1' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MdSearch size={16} />
                    <Box component="span" sx={{ color: '#94a3b8' }}>Quick Actions</Box>
                </Box>
                <Box
                    component="span"
                    sx={{
                        display: 'none',
                        '@media (min-width:768px)': { display: 'inline-flex' },
                        alignItems: 'center',
                        gap: '2px',
                        px: '0.375rem',
                        py: '0.125rem',
                        bgcolor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.25rem',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                    }}
                >
                    {IS_MAC ? '⌘K' : 'Ctrl K'}
                </Box>
            </Box>

            {open && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: '0.75rem',
                        right: '0.75rem',
                        top: '4rem',
                        width: 'auto',
                        bgcolor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        zIndex: 1300,
                        overflow: 'hidden',
                        maxHeight: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                        animation: 'qa-slideDown 200ms ease-out',
                        '@keyframes qa-slideDown': {
                            from: { opacity: 0, transform: 'translateY(-10px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        '@media (min-width:640px)': {
                            position: 'absolute',
                            left: 0,
                            top: 'auto',
                            right: 'auto',
                            mt: 1,
                            width: '360px',
                            maxHeight: '420px',
                        },
                    }}
                >
                    <Box sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                        <Box
                            component="input"
                            ref={inputRef}
                            type="text"
                            placeholder="Search modules, pages..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            sx={{
                                width: '100%',
                                px: '0.75rem',
                                py: '0.5rem',
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                outline: 'none',
                                fontFamily: 'inherit',
                                '&:focus': {
                                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                                    borderColor: '#2563eb',
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ overflowY: 'auto', flex: 1, p: 1 }}>
                        {groupedResults.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center', fontSize: '0.875rem', color: '#94a3b8' }}>
                                No matching pages
                            </Box>
                        ) : (
                            groupedResults.map(({ group, icon: Icon, items }) => (
                                <Box key={group} sx={{ mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5 }}>
                                        {Icon && (
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '0.375rem',
                                                    bgcolor: '#eff6ff',
                                                    color: '#2563eb',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Icon size={13} />
                                            </Box>
                                        )}
                                        <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {group}
                                        </Box>
                                    </Box>
                                    {items.map((action) => (
                                        <Box
                                            component="button"
                                            key={action.path}
                                            onClick={() => handleSelect(action.path)}
                                            sx={{
                                                width: '100%',
                                                display: 'block',
                                                textAlign: 'left',
                                                pl: '2.75rem',
                                                pr: 1.25,
                                                py: 0.75,
                                                borderRadius: '0.5rem',
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                cursor: 'pointer',
                                                fontFamily: 'inherit',
                                                fontSize: '0.875rem',
                                                color: '#334155',
                                                transition: 'background-color 150ms',
                                                '&:hover': { bgcolor: '#f8fafc' },
                                            }}
                                        >
                                            {action.subLabel || action.label}
                                        </Box>
                                    ))}
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default QuickActions;
